/**
 * Conversation Error Helpers
 *
 * Bridges Result-pattern errors from endpoint-errors to conversation-domain
 * ErrorInfo used by the chat UI. Separated from endpoint-errors to keep
 * that module free of conversation-specific types.
 */

import { transportErrorMessage, type TransportError, type DomainErrorBase } from '../generated/api/endpoint-errors';
import type { ErrorInfo } from '../types/conversation';
import i18n from '../i18n';

/**
 * Convert a failed Result (domain or transport) to conversation-domain ErrorInfo.
 * Use for all HTTP API catch blocks (conversation, session, analysis).
 */
export function resultToErrorInfo(
  result: { ok: false; kind: 'domain'; error: DomainErrorBase & { code: string; category: string } }
    | { ok: false; kind: 'transport'; error: TransportError }
): ErrorInfo {
  if (result.kind === 'domain') {
    const { code, category } = result.error;
    const categoryToType: Record<string, ErrorInfo['type']> = {
      VALIDATION: 'validation',
      BUSINESS: 'business',
      AUTH: 'auth',
      UNEXPECTED: 'server',
    };
    const type = categoryToType[category] || 'unknown';

    return {
      type,
      code,
      message: result.error.message,
      retryable: category === 'UNEXPECTED',
      referenceId: (result.error as { referenceId?: string }).referenceId,
      fieldErrors: (result.error as { fieldErrors?: Record<string, string[]> }).fieldErrors,
    };
  }

  // Transport error
  const transportType: Record<string, ErrorInfo['type']> = {
    OFFLINE: 'network',
    TIMEOUT: 'timeout',
    SERVER_UNREACHABLE: 'network',
    SERVER_ERROR: 'server',
    UNKNOWN: 'unknown',
  };

  return {
    type: transportType[result.error.code] || 'unknown',
    code: result.error.code,
    message: transportErrorMessage(result.error),
    retryable: result.error.retryable,
    referenceId: (result.error as { referenceId?: string }).referenceId,
  };
}

/**
 * Convert a thrown error (non-API, e.g. socket setup failures, validation)
 * to conversation-domain ErrorInfo. Use only when the error is a thrown exception,
 * not a Result-pattern failure.
 */
export function thrownErrorToErrorInfo(error: unknown): ErrorInfo {
  const message = error instanceof Error ? error.message : i18n.t('errors.api.UNKNOWN');
  return {
    type: 'unknown',
    message,
    retryable: true,
  };
}

/**
 * Standard ErrorInfo for non-HTTP analysis failures (socket events, client-side timeouts).
 * Only use when there's no error object to parse — for HTTP errors, use resultToErrorInfo.
 *
 * Function (not constant) because i18n.t() must be called at error time, not module-load time.
 */
export function createAnalysisError(
  code: 'ANALYSIS_FAILED' | 'ANALYSIS_TIMEOUT' = 'ANALYSIS_FAILED'
): ErrorInfo {
  return {
    type: 'server',
    code,
    message: i18n.t('chat.messages.analysisUnavailable'),
    retryable: true,
  };
}
